import type {
  CardField,
  CardVisibility,
  ProtectedFieldSelection,
  ProtectedVaultRecord,
  ReunificationCard,
  ReunificationCardKind,
  SupportedLocale,
} from "@safesphere/contracts";

import type { QuickPlanData } from "./familyRepository.js";

export const messageTemplateIds = [
  "I_AM_SAFE",
  "MOVING_TO_MEETING_POINT",
  "CONTACT_THIS_PERSON",
  "NEED_ASSISTANCE",
  "WE_ARE_SEPARATED",
] as const;
export type MessageTemplateId = (typeof messageTemplateIds)[number];

const templates: Readonly<Record<SupportedLocale, Readonly<Record<MessageTemplateId, string>>>> = {
  th: {
    I_AM_SAFE: "ฉันปลอดภัย นี่เป็นข้อความสมมติสำหรับทดสอบ",
    MOVING_TO_MEETING_POINT: "ฉันกำลังไปยังจุดนัดพบสมมติ",
    CONTACT_THIS_PERSON: "โปรดติดต่อผู้ติดต่อสมมติที่ระบุในบัตรนี้",
    NEED_ASSISTANCE: "ฉันต้องการความช่วยเหลือ นี่เป็นข้อความสมมติ",
    WE_ARE_SEPARATED: "ขณะนี้เราแยกกันอยู่ โปรดใช้แผนสมมติที่ตกลงไว้",
  },
  en: {
    I_AM_SAFE: "I am safe. This is a fictional test message.",
    MOVING_TO_MEETING_POINT: "I am moving to the fictional meeting point.",
    CONTACT_THIS_PERSON: "Please contact the fictional person listed on this card.",
    NEED_ASSISTANCE: "I need assistance. This is a fictional test message.",
    WE_ARE_SEPARATED: "We are separated. Please use the agreed fictional plan.",
  },
};

export function communicationTemplates(locale: SupportedLocale) {
  return messageTemplateIds.map((id) => ({ id, text: templates[locale][id] }));
}

export interface CardBuildInput {
  readonly kind: ReunificationCardKind;
  readonly locale: SupportedLocale;
  readonly quick: QuickPlanData;
  readonly selectedQuickFieldIds: readonly string[];
  readonly protectedRecords: readonly ProtectedVaultRecord[];
  readonly protectedSelections: readonly ProtectedFieldSelection[];
  readonly vaultUnlocked: boolean;
  readonly sensitiveVisibilityConfirmed: boolean;
  readonly editedTemplate?: string;
  readonly now?: string;
}

function title(kind: ReunificationCardKind, locale: SupportedLocale): string {
  const values: Record<ReunificationCardKind, readonly [string, string]> = {
    FAMILY_STATUS: ["สถานะครอบครัวสมมติ", "Fictional family status"],
    OUT_OF_AREA_CONTACT: ["ผู้ติดต่อนอกพื้นที่สมมติ", "Fictional out-of-area contact"],
    MEETING_POINT: ["สรุปจุดนัดพบสมมติ", "Fictional meeting-point summary"],
    CHILD_SUPPORT: ["บัตรช่วยเหลือเด็กสมมติ", "Fictional child-support card"],
    OLDER_ADULT_SUPPORT: ["บัตรช่วยเหลือผู้สูงอายุสมมติ", "Fictional older-adult support card"],
    PET_REUNIFICATION: ["บัตรรวมตัวสัตว์เลี้ยงสมมติ", "Fictional pet reunification card"],
    COMMUNICATION_TEMPLATE: ["ข้อความสื่อสารสมมติ", "Fictional communication message"],
  };
  return values[kind][locale === "th" ? 0 : 1];
}

export function availableQuickFields(
  quick: QuickPlanData,
  locale: SupportedLocale,
): readonly CardField[] {
  const fields: CardField[] = [
    {
      id: "plan.label",
      label: locale === "th" ? "ชื่อแผน" : "Plan label",
      value: quick.plan.label,
      source: "QUICK_ACCESS",
      sensitive: false,
    },
  ];
  for (const contact of quick.contacts)
    fields.push({
      id: `contact:${contact.id}`,
      label: locale === "th" ? "ผู้ติดต่อ" : "Contact",
      value: `${contact.name} — ${contact.contactValue}`,
      source: "QUICK_ACCESS",
      sensitive: false,
    });
  for (const point of quick.meetingPoints)
    fields.push({
      id: `meeting:${point.id}`,
      label: locale === "th" ? "จุดนัดพบ" : "Meeting point",
      value: `${point.label} — ${point.locationNote}`,
      source: "QUICK_ACCESS",
      sensitive: false,
    });
  return fields;
}

export function availableProtectedFields(
  records: readonly ProtectedVaultRecord[],
  locale: SupportedLocale,
): readonly CardField[] {
  const fields: CardField[] = [];
  for (const record of records) {
    if (record.kind === "MEMBER") {
      fields.push(
        {
          id: `${record.id}:displayName`,
          label: locale === "th" ? "ชื่อสมาชิก" : "Member name",
          value: record.displayName,
          source: "PROTECTED_VAULT",
          sensitive: true,
        },
        {
          id: `${record.id}:allergyPlaceholder`,
          label: locale === "th" ? "ข้อมูลแพ้สมมติ" : "Fictional allergy field",
          value: record.allergyPlaceholder,
          source: "PROTECTED_VAULT",
          sensitive: true,
        },
        {
          id: `${record.id}:medicationPlaceholder`,
          label: locale === "th" ? "ข้อมูลยาสมมติ" : "Fictional medication field",
          value: record.medicationPlaceholder,
          source: "PROTECTED_VAULT",
          sensitive: true,
        },
      );
    } else if (record.kind === "PET") {
      fields.push(
        {
          id: `${record.id}:displayName`,
          label: locale === "th" ? "ชื่อสัตว์เลี้ยง" : "Pet name",
          value: record.displayName,
          source: "PROTECTED_VAULT",
          sensitive: true,
        },
        {
          id: `${record.id}:careNotePlaceholder`,
          label: locale === "th" ? "ข้อมูลดูแลสมมติ" : "Fictional care field",
          value: record.careNotePlaceholder,
          source: "PROTECTED_VAULT",
          sensitive: true,
        },
      );
    }
  }
  return fields;
}

function defaultVisibility(kind: ReunificationCardKind): CardVisibility {
  return kind === "CHILD_SUPPORT" || kind === "OLDER_ADULT_SUPPORT" || kind === "PET_REUNIFICATION"
    ? "PRIVATE_DEVICE_ONLY"
    : "SUITABLE_TO_SHARE";
}

export function buildReunificationCard(input: CardBuildInput): ReunificationCard {
  const quickFields = availableQuickFields(input.quick, input.locale).filter((field) =>
    input.selectedQuickFieldIds.includes(field.id),
  );
  const protectedFields =
    input.vaultUnlocked && input.sensitiveVisibilityConfirmed
      ? availableProtectedFields(input.protectedRecords, input.locale).filter((field) =>
          input.protectedSelections.some(
            (selection) => `${selection.recordId}:${selection.field}` === field.id,
          ),
        )
      : [];
  const templateField: CardField[] =
    input.kind === "COMMUNICATION_TEMPLATE" && input.editedTemplate?.trim()
      ? [
          {
            id: "template.message",
            label: input.locale === "th" ? "ข้อความ" : "Message",
            value: input.editedTemplate.trim(),
            source: "EDITED_TEMPLATE",
            sensitive: false,
          },
        ]
      : [];
  return {
    id: `preview:${input.kind}`,
    kind: input.kind,
    locale: input.locale,
    title: title(input.kind, input.locale),
    visibility: protectedFields.length > 0 ? "INCLUDES_SENSITIVE" : defaultVisibility(input.kind),
    fictionalFixture: input.quick.plan.fictionalFixture,
    fields: [...quickFields, ...protectedFields, ...templateField],
    createdAt: input.now ?? new Date().toISOString(),
  };
}

export function cardAccessibleText(card: ReunificationCard): string {
  const lines = [card.title, `Visibility: ${card.visibility}`];
  if (card.fictionalFixture) lines.push("FICTIONAL TEST FIXTURE");
  for (const field of card.fields) lines.push(`${field.label}: ${field.value}`);
  return lines.join("\n");
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

export function cardPrintHtml(card: ReunificationCard): string {
  const fields = card.fields
    .map((field) => `<dt>${escapeHtml(field.label)}</dt><dd>${escapeHtml(field.value)}</dd>`)
    .join("");
  return `<!doctype html><html lang="${card.locale}"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width"><title>${escapeHtml(card.title)}</title><style>body{font:18px/1.5 system-ui;margin:2rem;color:#17231e}main{max-width:42rem;margin:auto}header{border-bottom:3px solid #245b47}dl{display:grid;grid-template-columns:1fr 2fr;gap:.75rem}dt{font-weight:700}dd{margin:0}.fixture{padding:.75rem;background:#f4e9bd}@media print{button{display:none}}</style></head><body><main><header><h1>${escapeHtml(card.title)}</h1><p>${escapeHtml(card.visibility)}</p></header>${card.fictionalFixture ? '<p class="fixture">FICTIONAL TEST FIXTURE</p>' : ""}<dl>${fields}</dl></main></body></html>`;
}

function browserWriteText(): ((value: string) => Promise<void>) | undefined {
  const clipboard = (navigator as unknown as { readonly clipboard?: Clipboard }).clipboard;
  return clipboard === undefined ? undefined : clipboard.writeText.bind(clipboard);
}

function browserShare(): ((data: ShareData) => Promise<void>) | undefined {
  const share = (navigator as unknown as { readonly share?: (data: ShareData) => Promise<void> })
    .share;
  return share === undefined ? undefined : share.bind(navigator);
}

export async function copyCardText(
  text: string,
  writeText: ((value: string) => Promise<void>) | undefined = browserWriteText(),
): Promise<"COPIED" | "UNAVAILABLE"> {
  if (writeText === undefined) return "UNAVAILABLE";
  try {
    await writeText(text);
    return "COPIED";
  } catch {
    return "UNAVAILABLE";
  }
}

export async function shareCardText(
  titleValue: string,
  text: string,
  share: ((data: ShareData) => Promise<void>) | undefined = browserShare(),
  writeText?: (value: string) => Promise<void>,
): Promise<"SHARED" | "COPIED_FALLBACK" | "UNAVAILABLE"> {
  if (share !== undefined) {
    try {
      await share({ title: titleValue, text });
      return "SHARED";
    } catch {
      // A local clipboard fallback is attempted below.
    }
  }
  return (await copyCardText(text, writeText)) === "COPIED" ? "COPIED_FALLBACK" : "UNAVAILABLE";
}
